import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { WebSdkChatEvent } from "@gleanwork/web-sdk";
import EventLog, { EventLogEntry } from "../components/EventLog";
import { EmbedConfigContext, authOptionsKey, baseOptionsKey } from "../EmbedConfigContext";
import { EmbeddedSearchWidget } from "../types";
import useAuthProvider from "../useAuthProvider";

interface ChatLocation {
  type: 'chat' | 'agent';
  id: string | undefined;
}

const chatEventNames: WebSdkChatEvent['name'][] = [
  'chat:page_view',
  'chat:name_update',
  'chat:id_update',
  'chat:location_update',
  'chat:action_click',
];

const ChatPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const nextEventId = useRef(0);
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [searchParams] = useSearchParams();
  const {config} = useContext(EmbedConfigContext)
  const authParams = useAuthProvider(config[authOptionsKey], config[baseOptionsKey].backend)
  const [ currentChatLocation, setCurrentChatLocation ] = useState<ChatLocation | undefined>(undefined);

  useEffect(() => {
    if (!window.GleanWebSDK) return;

    const chatCustomConfig = {
      ...config[baseOptionsKey],
      ...config[EmbeddedSearchWidget.Chat]
    }

    const handler = containerRef.current && window.GleanWebSDK.renderChat(containerRef.current, {
      ...chatCustomConfig,
      ...authParams,
      // Add overrides to the custom config here
      themeVariant: chatCustomConfig.themeVariant,
      chatId: searchParams.get("chatId") ?? chatCustomConfig.chatId,
    })

    const handleEvent = (event: WebSdkChatEvent) => {
      const { name, ...payload } = event;
      const entry: EventLogEntry = {
        id: nextEventId.current++,
        timestamp: Date.now(),
        name,
        payload,
      };
      setEvents((previousEvents) => [...previousEvents, entry]);
      if (event.name === 'chat:location_update') {
        setCurrentChatLocation({ type: event.type, id: event.id });
      }
    };

    chatEventNames.forEach((name) => {
      handler?.on(name, handleEvent);
    });

    return () => {
      chatEventNames.forEach((name) => {
        handler?.off(name, handleEvent);
      });
    };
  }, [searchParams, config, authParams]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px" }}>
        <span className="text-sm rounded-md p-1 my-2 inline-block">Current {currentChatLocation?.type}: {String(currentChatLocation?.id)}</span>
        <EventLog events={events} onClear={() => setEvents([])} />
      </div>
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, width: "100%", position: "relative" }} />
    </div>
  );
};

export default ChatPage;
